import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";

import { useAppDispatch, useAppSelector } from "store";
import { getRiskPlayersList, handlePaginate } from "store/risk/players/slice";

import { openModal } from "store/modals/slice";
import { ModalPlayerTabs, ModalTypes } from "configs/types";
import Pagination from "components/Pagination";
import PlayersRiskFilter from "./components/PlayersRiskFilter";
import { playersListLimits } from "configs/limits";
import { useTranslation } from "react-i18next";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";

const KYC_STATUS_DISPLAY: Record<
  string,
  { label: string; badgeStatus: string }
> = {
  none: { label: "Not Started", badgeStatus: "none" },
  missing: { label: "Required", badgeStatus: "warning" },
  pending: { label: "Under Review", badgeStatus: "pending" },
  approved: { label: "Approved", badgeStatus: "approved" },
  requiresreview: { label: "Needs Review", badgeStatus: "warning" },
  requiresreupload: { label: "Action Required", badgeStatus: "warning" },
  rejected: { label: "Rejected", badgeStatus: "rejected" },
  expired: { label: "Expired", badgeStatus: "expired" },
};

const RiskPlayersList: React.FC = () => {
  const { t } = useTranslation();
  const data = useAppSelector((store) => store.riskPlayers.list);

  const isListFetched = useAppSelector(
    (store) => store.riskPlayers.isListFetched,
  );
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: 'riskPlayers',
    isListFetched
  });

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const totalItems = useAppSelector((store) => store.riskPlayers.totalItems);
  const totalPages = useAppSelector((store) => store.riskPlayers.totalPages);
  const activePage = useAppSelector((store) => store.riskPlayers.activePage);
  const [currentLimit, setLimit] = useState<number>(playersListLimits[0]);
  const [search, setSearch] = useState<string>("");
  const [searchId, setSearchId] = useState<string>("");

  const [risks, setRisks] = useState<
    { value: string | number; label: string }[]
  >([]);

  const [statuses, setStatuses] = useState<
    { value: string | number; label: string }[]
  >([]);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getRiskPlayersList({
          limit: currentLimit,
          offset: activePage - 1,
          email: search === "" ? undefined : search,
          userIds: searchId ? [Number(searchId)] : [],
          riskStatuses: risks.map((el) => String(el.value)),
          kycStatuses: statuses.map((el) => String(el.value)),
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit]);

  const renderStatus = (value: string, isRiskLevel = false) => {
    const statusDisplay = KYC_STATUS_DISPLAY[value.toLowerCase()];

    return (
      <span
        className={`badge badge--${
          statusDisplay?.badgeStatus ?? value.toLowerCase()
        } kyc-documents-status-badge${
          isRiskLevel ? " kyc-documents-status-badge--risk-level" : ""
        }`}
      >
        {statusDisplay?.label ?? value}
      </span>
    );
  };

  const handleOpenEditModal = (userId: number) => {
    dispatch(openModal({ activeModal: ModalTypes.player }));

    navigate(
      `${location.pathname}?activeModal=${ModalTypes.player}&entityId=${userId}&tab=${ModalPlayerTabs.verification}`,
    );
  };

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };

  const handleFilterSearch = (s: string) => {
    setSearch(s);
  };

  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  // REQUIRES REVIEW FILTRATION

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <PlayersRiskFilter
        searchValue={search}
        handleSearch={handleFilterSearch}
        searchId={searchId}
        handleSearchId={setSearchId}
        selectedRisks={risks}
        handleUpdateRisks={(e) => setRisks(e)}
        selectedStatuses={statuses}
        handleUpdateStatuses={(e) => setStatuses(e)}
        handleApply={handleApply}
      />

      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box"></div>
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
        </div>

   
          {!isListFetched && (
                  <>
                    <DataTableSkeleton columnsCount={6} rowsCount={10} />
                    <PaginationSkeleton />
                  </>
                )}

        {isListFetched && (
          <div className="game-table-wrap table">
            {data.length === 0 && (
              <EmptyState message={t("noRiskPlayersFound")} />
            )}

            {data.length > 0 && (
              <Table
                data={data}
                idField="userId"
                hasCheckbox={false}
                hasOptions={true}
                hasDelete={false}
                onEdit={(id) => {
                  handleOpenEditModal(id);
                }}
                columns={{
                  hiddenColumns: [],
                }}
                customColumns={(row) => ({
                  riskLevel: renderStatus(row.riskLevel, true),
                  kycStatus: renderStatus(row.kycStatus),
                })}
              />
            )}
          </div>
        )}

        {isListFetched && data.length > 0 && (
          <Pagination
            activePage={activePage}
            limit={currentLimit}
            totalItems={totalItems}
            totalPages={totalPages}
            limitsArray={playersListLimits}
            handleNextClick={handleNextClick}
            handlePrevClick={handlePrevClick}
            handleLimitClick={(e) => {
              setLimit(e);
              dispatch(handlePaginate(1));
            }}
          />
        )}
      </div>
    </div>
  );
};

export default RiskPlayersList;
